import React, { useContext, useEffect, useState, useRef } from 'react';
import Modal from 'react-modal';
import {
  useAgoraChat,
  useAgoraVideo,
  useContainerSize,
  RootContext,
  SvgArrowIcon,
  SvgMicIcon,
  SvgMicMuteIcon,
  SvgCameraIcon,
  SvgCameraMuteIcon,
  SvgChatIcon,
  SvgShareIcon,
  IsWebRTC,
  SYSTEM_CALLS,
  Chat,
  VideoMembers,
  ConfirmModal,
  SharingUrl,
  MembersContext,
  MembersDispatchContext,
  UserApiV1TreatmentPath,
  AgoraUserApiV1TreatmentPath,
  useChatDisplayStatus,
} from '../../../index';

export const TreatmentVideoView = () => {
  const context = useContext(RootContext);
  const contextRef = useRef(null);
  contextRef.current = context; // イベントリスナー内で参照する場合はこちらを使う
  const members = useContext(MembersContext);
  const membersRef = useRef(null);
  membersRef.current = members; // イベントリスナー内で参照する場合はこちらを使う
  const dispatchMembers = useContext(MembersDispatchContext);
  const mainMember = members.find(m => m.mained);

  const isSP = navigator.userAgent.indexOf('iPhone') > 0 ||
    navigator.userAgent.indexOf('iPad') > 0 ||
    navigator.userAgent.indexOf('iPod') > 0;
  const mainTrackElementId = 'main_track';
  const MODAL_STATUSES = {
    EXITING: 0,
    ALERT: 2,
  };

  const agoraUser = useRef(null);

  const [isStarted, setIsStarted] = useState(false); // 診療中
  const [modalStatus, setModalStatus] = useState(); // 表示中のモーダル
  const [isLoading, setIsLoading] = useState(true); // 右ペインのローディング表示
  const [isShareArea, setIsShareArea] = useState(false); // 共有URLエリア
  const [openDeviceArea, setOpenDeviceArea] = useState(); // カメラ、オーディオの設定画面
  const [alert, setAlert] = useState({ title: '', body: '', onOk: null });
  const [devices, setDevices] = useState({ audios: [], cameras: [], speekers: [] });

  const chat = useAgoraChat({
    agoraUser: agoraUser,
    onMemberLeft: (uid) => {
      // メンバーにdoctorがいないならリロード
      if (!membersRef.current.some(m => m.uid.startsWith('doctor'))) {
        location.reload();
      }
    },
  });
  const video = useAgoraVideo({
    agoraUser: agoraUser,
    mainTrackElementId: mainTrackElementId,
    chatRenewToken: async () => {
      return await chat.renewToken();
    },
  });

  const containerSize = useContainerSize({
    clientMarginHeight: 102,
  });
  const chatDisplayStatus = useChatDisplayStatus();

  const cameraPanel = useRef();
  const audioPanel = useRef();
  const sharePanel = useRef();

  const setSystemCallHandler = () => {
    chat.onReceiveSystemCall(async (systemCall, targetUid) => {
      switch (systemCall) {
      case SYSTEM_CALLS.PAUSE:
        await leave();
        location.reload();
        break;
      case SYSTEM_CALLS.FINISH:
        await leave();
        location.href = contextRef.current.url_location.diagnose;
        break;
      case SYSTEM_CALLS.KICK:
        if (targetUid === agoraUser.current.uid) {
          await leave();
          location.href = contextRef.current.url_location.leave;
        }
        break;
      case SYSTEM_CALLS.ACCEPT:
        if (targetUid === agoraUser.current.uid) {
          await video.join();
          dispatchMembers({
            type: 'doctor_accept',
            uid: targetUid,
          });
          setIsStarted(true);
          // 自分をサブ画面に初期表示
          video.playOnSub();
          await video.publish();
        }
        break;
      case SYSTEM_CALLS.REJECT:
        if (targetUid === agoraUser.current.uid) {
          await leave();
          location.href = contextRef.current.url_location.exit;
        }
        break;
      }
    });
  };

  /**
   *
   */
  const leave = async () => {
    try {
      await chat.leave();
      await video.leave();
    } catch {
      // 何もしない
    }
  };

  /**
   * 共有URLエリアを表示・非表示
   * @param {*} e
   */
  const toggleShareUrl = (e) => {
    e.preventDefault();
    setIsShareArea(!isShareArea);
  };

  const openExitModal = () => {
    setModalStatus(MODAL_STATUSES.EXITING);
  };

  const exit = async () => {
    await leave();
    location.href = context.url_location.exit;
  };

  // オーディオとカメラの設定画面ON/OFF
  const onSettingArea = (area) => {
    area === openDeviceArea ? setOpenDeviceArea(null) : setOpenDeviceArea(area);
  };

  /**
   * カメラデバイスの切り替え
   */
  const changeCameraDevice = async (e) => {
    const cameraId = e.target.value;
    await video.setCameraDevice(cameraId);
  };

  /**
   * マイクデバイスの切り替え
   */
  const changeAudioDevice = async (e) => {
    const audioId = e.target.value;
    await video.setAudioDevice(audioId);
  };

  /**
   * スピーカーデバイスの切り替え
   */
  const changeSpeekerDevice = async (e) => {
    const speekerId = e.target.value;
    await video.setSpeekerDevice(speekerId);
  };

  /**
   * カメラ停止/開始
   */
  const onMuteVideo = async () => {
    await video.muteVideo();
  };

  /**
   * マイクミュート/解除
   */
  const onMuteAudio = async () => {
    await video.muteAudio();
  };

  /**
   * アラートモーダルの表示
   */
  const showAlert = ({ title = context.t('Agora.fatal_error'), body = context.t('Agora.fatal_error_annotation'), onOk = null } = {}) => {
    setAlert({ title: title, body: body, onOk: onOk });
    setModalStatus(MODAL_STATUSES.ALERT);
  };

  const closeAlert = async () => {
    context.exception(new Error(alert.body || alert.title));
    if (alert.onOk) {
      alert.onOk();
      return;
    }
    await leave();
    location.reload();
  };

  const renderWaiting = () => {
    return (
      <div className={isLoading ? 'p-examination p-loading p-loading--2x' : 'p-examination' }>
        <div className="p-examination--content">
          {context.formatSimple(context.t('Agora.please_wait'))}
        </div>
        { renderSharingUrl() }
      </div>
    );
  };

  const renderSharingUrl = () => {
    return <SharingUrl
      onClose={ toggleShareUrl }
      shareLinkText={ context.t('Agora.share_link') }
      copyText={ context.t('Agora.copy') }
      copiedText={ context.t('Agora.copy_toast') }
    />;
  };

  useEffect(() => {
    let ignore = false;
    document.body.className = 'full';
    setIsLoading(true);
    if (!IsWebRTC()) {
      setIsLoading(false);
      showAlert({
        body: context.t('Agora.not_support'),
        onOk: async () => {
          await leave();
          location.href = location.pathname.split('/').slice(0, 3).join('/');
        },
      });
      return;
    }

    window.addEventListener('beforeunload', async () => { await leave(); });
    window.addEventListener('pageshow', ev => {
      // キャッシュ利用の場合は、リロードを実施する
      const navigation = performance.getEntriesByType('navigation');
      if (navigation.length > 0 && navigation[0].type === 'back_forward') {
        location.reload();
      } else if (ev.persisted) {
        location.reload();
      }
    });

    // リサイズイベント取得
    Modal.setAppElement('body');

    const treatmentId = location.pathname.split('/').filter(s => s !== '').at(-2);
    const ready = async () => {
      await Promise.all([
        context.fetch(UserApiV1TreatmentPath(treatmentId)),
        context.fetch(AgoraUserApiV1TreatmentPath(treatmentId)),
      ]).then(body => {
        if (!ignore) {
          context.onStateUpdate({ ...body[0].result });
          agoraUser.current = { ...body[1].result };
        }
      });

      const initDevices = await video.getDevices();
      setDevices(initDevices);

      // チャットでシステム通知を受け取るため、先にjoinしておく
      await chat.join();

      if (!ignore) {
        dispatchMembers({
          type: 'patient_joined',
          uid: agoraUser.current.uid,
          name: context.t('Agora.your'),
        });
        setSystemCallHandler();
      }
    };

    try {
      ready();
    } catch (e) {
      showAlert();
    } finally {
      setIsLoading(false);
    }

    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    isLoading ? context.showLoading() : context.hideLoading();
  }, [isLoading]);

  return (
    <div>
      {
        isStarted
          ? <div className='p-video'>
            <div className={isSP ? 'p-video-container p-video-container--sp' : 'p-video-container'}>
              <div>
                <div id={ mainTrackElementId } className='p-screen' style={containerSize}>
                  { mainMember?.muted && <div className="p-video--videomute">{SvgCameraIcon}</div> }
                </div>
              </div>
            </div>
            {
              isSP &&
              <div className="p-language-area">
                <div className="p-language-area--text-area">
                  <div>
                    { context.lang === 'ja' ? 'JP' : (<a onClick={ () => {
                      context.language('ja');
                    }}>JP</a>) }
                    &nbsp;/&nbsp;
                    { context.lang !== 'ja' ? 'EN' : (<a onClick={ () => {
                      context.language('en');
                    }}>EN</a>) }
                  </div>
                </div>
              </div>
            }
            <VideoMembers
              video={ video }
              chat={ chat }
              isSP={ isSP }
            />
            <div className='p-menu-area'>
              <div className="level-left">
                <div id="audioPanel" className="has-text-centered p-menu-area__panel">
                  <a onClick={onMuteAudio} className="fz12 p-menu-area__panel-button">
                    { video.isMuteAudio ? SvgMicIcon : SvgMicMuteIcon }
                    <p className="fz10">{video.isMuteAudio ? context.t('Agora.not_mute') : context.t('Agora.mute')}</p>
                  </a>
                  <a onClick={() => onSettingArea('audio')} className="fz12 p-menu-area__panel-button p-menu-area__panel-arrow">{ SvgArrowIcon }</a>
                </div>
                <div id="cameraPanel" className="has-text-centered p-menu-area__panel">
                  <a onClick={onMuteVideo} className="fz12 p-menu-area__panel-button">
                    { video.isMuteVideo ? SvgCameraIcon : SvgCameraMuteIcon }
                    <p className="fz10">{video.isMuteVideo ? context.t('Agora.play_video') : context.t('Agora.stop_video')}</p>
                  </a>
                  <a onClick={() => onSettingArea('camera')} className="fz12 p-menu-area__panel-button p-menu-area__panel-arrow">{ SvgArrowIcon }</a>
                </div>
              </div>
              <div className="level-center">
                <div ref={sharePanel} className="has-text-centered p-menu-area__panel">
                  <a onClick={toggleShareUrl} className="fz12 p-menu-area__panel-button">
                    { SvgShareIcon }
                    <p className="fz10">{context.t('Agora.share_link')}</p>
                  </a>
                </div>
                <div className="has-text-centered p-menu-area__panel">
                  <a onClick={chatDisplayStatus.open} className={`fz12 p-menu-area__panel-button ${chatDisplayStatus.unreadBadge ? 'unread' : ''}` }>
                    { SvgChatIcon }
                    <p className="fz10">{context.t('Agora.chat')}</p>
                  </a>
                </div>
              </div>
              <div className="level-right">
                <div className="has-text-centered p-menu-area__panel">
                  <button onClick={openExitModal} className="p-menu-area__panel-button p-menu-area__panel--finish">
                    <p className="fz14 fw-br">{context.t('Agora.leave')}</p>
                  </button>
                </div>
              </div>
            </div>
            <div>
              {
                openDeviceArea === 'camera' &&
                <div style={{ left: cameraPanel.current?.offsetLeft }} className='p-video-menu__item p-video-menu__item__bg'>
                  <p className="p-video-menu__item-label">{context.t('Agora.camera')}</p>
                  {devices.cameras.map(item => {
                    return (
                      <div key={item.id} className="p-video-menu__item-option">
                        <input type="radio" name="camera" id={`camera-${item.id}`} value={item.id} checked={item.id === video.activeCameraId} onChange={changeCameraDevice} />
                        <label htmlFor={`camera-${item.id}`}>{item.label}</label>
                      </div>
                    );
                  })}
                </div>
              }
              {
                openDeviceArea === 'audio' &&
                <div style={{ left: audioPanel.current?.offsetLeft }} className='p-video-menu__item p-video-menu__item__bg'>
                  <p className="p-video-menu__item-label">{context.t('Agora.mic')}</p>
                  {devices.audios.map(item => {
                    return (
                      <div key={item.id} className="p-video-menu__item-option">
                        <input type="radio" name="mic" id={`mic-${item.id}`} value={item.id} checked={item.id === video.activeAudioId} onChange={changeAudioDevice} />
                        <label htmlFor={`mic-${item.id}`}>{item.label}</label>
                      </div>
                    );
                  })}
                  <hr />
                  <p className="p-video-menu__item-label">{context.t('Agora.speaker')}</p>
                  {devices.speekers.map(item => {
                    return (
                      <div key={item.id} className="p-video-menu__item-option">
                        <input type="radio" name="speeker" id={`speeker-${item.id}`} value={item.id} checked={item.id === video.activeSpeekerId} onChange={changeSpeekerDevice} />
                        <label htmlFor={`speeker-${item.id}`}>{item.label}</label>
                      </div>
                    );
                  })}
                </div>
              }
              {
                isShareArea &&
                  <div style={{ left: sharePanel.current?.offsetLeft - 90 }} className='p-video-menu__item'>
                    { renderSharingUrl() }
                  </div>
              }
            </div>
            {
              !isLoading &&
              <Chat
                chat={ chat }
                agoraUser={ agoraUser }
                chatDisplayStatus={ chatDisplayStatus }
                headerText={ context.t('Agora.chat') }
                placeHolderText={ context.t('Agora.input_message') }
              />
            }
          </div>
          : renderWaiting()
      }
      <ConfirmModal
        isOpen={ modalStatus === MODAL_STATUSES.EXITING }
        title={ context.t('Agora.leave_annotation') }
        onOk={ exit }
        onCancel={ async () => setModalStatus() }
        option={
          { ok: context.t('Agora.leave') }
        }
      />
      <ConfirmModal
        isOpen={ modalStatus === MODAL_STATUSES.ALERT }
        body={ alert.body }
        onOk={ closeAlert }
        option={
          {
            showCancel: false,
          }
        }
      />
    </div>
  );
};
